import { useState, useEffect } from 'react';
import api from '../../../services/api';
import { Plus, Edit2, Trash2, Check, Copy } from 'lucide-react';
import { useToast } from '../../../components/common/ToastContainer';
import ConfirmDialog from '../../../components/common/ConfirmDialog';
import TemplateEditor from './TemplateEditor';
import { usePermissions } from '../../../hooks/usePermissions';
import { useAuthStore } from '../../../stores/authStore';
import { useTenantStore } from '../../../stores/tenantStore';
import { USER_TYPES } from '../../../utils/constants';
import { tenantSettingsService } from '../../../services';

interface EmailTemplate {
    id: string;
    name: string;
    type: 'master' | 'content';
    subject?: string;
    is_default: boolean;
    updated_at: string;
    html_content: string;
    tenant_id?: string | null;
}

const EmailTemplates = () => {
    const [templates, setTemplates] = useState<EmailTemplate[]>([]);
    const [loading, setLoading] = useState(true);
    const [editingTemplate, setEditingTemplate] = useState<EmailTemplate | null>(null);
    const [isCreating, setIsCreating] = useState(false);
    const [confirmState, setConfirmState] = useState<{ show: boolean; templateId?: string }>({ show: false });
    const [actionLoading, setActionLoading] = useState(false);
    const toast = useToast();
    const { canCreate, canUpdate, canDelete } = usePermissions();
    const { user } = useAuthStore();
    const { getTenantId } = useTenantStore();
    const isSuperAdmin = user?.type === USER_TYPES.SUPER_ADMIN;
    // A TenantAdmin uses their own tenant; a ChannelAdmin has none of its own
    // (tenantId: null) and operates on the topbar selection. Reading user.tenantId
    // alone left a ChannelAdmin with '', so the mount effect skipped
    // loadTemplatePreference() — the only path that clears loadingPreference —
    // and the tab hung on "Loading templates..." forever.
    const tenantId = user?.tenantId || getTenantId() || '';

    // TenantAdmin: "use custom templates" toggle
    const [useCustom, setUseCustom] = useState(false);
    const [loadingPreference, setLoadingPreference] = useState(!isSuperAdmin);
    const [cloningDefaults, setCloningDefaults] = useState(false);

    useEffect(() => {
        if (!isSuperAdmin && tenantId) {
            loadTemplatePreference();
        } else {
            fetchTemplates();
        }
    }, []);

    // Fetch templates when useCustom changes (TenantAdmin only), skip while loading preference
    useEffect(() => {
        if (!isSuperAdmin && !loadingPreference) {
            fetchTemplates();
        }
    }, [useCustom]);

    const loadTemplatePreference = async () => {
        try {
            setLoadingPreference(true);
            const data = await tenantSettingsService.getCategory(tenantId, 'email_templates');
            const pref = data?.settings?.use_custom_templates;
            const isCustom = pref === true || pref === 'true';
            if (isCustom !== useCustom) {
                setUseCustom(isCustom); // triggers useEffect([useCustom])
            } else {
                fetchTemplates(); // same value, useEffect won't fire — fetch directly
            }
        } catch {
            // No tenant override = use system defaults
            if (useCustom !== false) {
                setUseCustom(false);
            } else {
                fetchTemplates();
            }
        } finally {
            setLoadingPreference(false);
        }
    };

    const toggleCustomTemplates = async (enable: boolean) => {
        try {
            await tenantSettingsService.updateCategory(tenantId, 'email_templates', {
                use_custom_templates: enable,
            });
            setUseCustom(enable);
            if (enable) {
                toast.success('Custom Templates Enabled', 'You can now create and manage custom email templates.');
            } else {
                toast.success('System Defaults Active', 'System default templates are now in use.');
            }
        } catch (err: any) {
            toast.error('Failed', err.message || 'Failed to update preference');
        }
    };

    const handleLoadDefaults = async () => {
        try {
            setCloningDefaults(true);
            await api.post('/email-templates/clone-system-defaults');
            toast.success('Defaults Loaded', 'System templates have been cloned. You can now customize them.');
            await fetchTemplates();
        } catch (err: any) {
            toast.error('Failed', err.message || 'Failed to clone system templates');
        } finally {
            setCloningDefaults(false);
        }
    };

    const fetchTemplates = async () => {
        try {
            setLoading(true);
            // TenantAdmin: scope determines which templates to show
            const scope = !isSuperAdmin && !useCustom ? 'system' : undefined;
            const params = new URLSearchParams();
            if (scope) params.set('scope', scope);
            const url = `/email-templates${params.toString() ? `?${params}` : ''}`;
            const response = await api.get(url);
            setTemplates(response.data);
        } catch (err) {
            toast.error('Error', 'Failed to load templates');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = (id: string) => {
        setConfirmState({ show: true, templateId: id });
    };

    const confirmDelete = async () => {
        if (!confirmState.templateId) return;

        setActionLoading(true);
        try {
            await api.delete(`/email-templates/${confirmState.templateId}`);
            toast.success('Deleted', 'Template deleted successfully');
            fetchTemplates();
            setConfirmState({ show: false });
        } catch (err) {
            toast.error('Error', 'Failed to delete template');
        } finally {
            setActionLoading(false);
        }
    };

    const handleCreate = () => {
        setEditingTemplate({
            id: '',
            name: 'New Template',
            type: 'content',
            subject: '',
            html_content: '<div>New content</div>',
            is_default: false,
            updated_at: '',
        });
        setIsCreating(true);
    };

    const handleEdit = (template: EmailTemplate) => {
        setEditingTemplate(template);
        setIsCreating(false);
    };

    const handleSave = async () => {
        await fetchTemplates();
        setEditingTemplate(null);
        setIsCreating(false);
    };

    if (editingTemplate) {
        return (
            <TemplateEditor
                template={editingTemplate}
                isNew={isCreating}
                onSave={handleSave}
                onCancel={() => setEditingTemplate(null)}
            />
        );
    }

    if (loading || loadingPreference) return <div>Loading templates...</div>;

    // TenantAdmin viewing system defaults = read-only
    const isReadOnly = !isSuperAdmin && !useCustom;

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h2 className="text-lg font-semibold text-gray-900">Email Templates</h2>
                <div className="flex items-center space-x-3">
                    {!isSuperAdmin && useCustom && (
                        <button
                            className="btn-secondary inline-flex items-center space-x-2"
                            onClick={handleLoadDefaults}
                            disabled={cloningDefaults}
                        >
                            <Copy className="w-4 h-4" />
                            <span>{cloningDefaults ? 'Loading...' : 'Load Defaults'}</span>
                        </button>
                    )}
                    {!isReadOnly && canCreate && (
                        <button className="btn-primary flex items-center space-x-2" onClick={handleCreate}>
                            <Plus className="w-4 h-4" />
                            <span>New Template</span>
                        </button>
                    )}
                </div>
            </div>

            {/* TenantAdmin: System Defaults vs Custom Templates toggle */}
            {!isSuperAdmin && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <h3 className="text-sm font-medium text-gray-900">Template Mode</h3>
                            <p className="text-sm text-gray-500 mt-0.5">
                                {useCustom
                                    ? 'Using custom templates for your tenant. Use "Load Defaults" to start from system templates.'
                                    : 'Using system default templates. Switch to custom mode to create your own.'}
                            </p>
                        </div>
                        <div className="flex items-center space-x-3">
                            <span className={`text-sm ${!useCustom ? 'font-medium text-gray-900' : 'text-gray-500'}`}>
                                System Defaults
                            </span>
                            <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={useCustom}
                                    onChange={(e) => toggleCustomTemplates(e.target.checked)}
                                    className="sr-only peer"
                                />
                                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                            </label>
                            <span className={`text-sm ${useCustom ? 'font-medium text-gray-900' : 'text-gray-500'}`}>
                                Custom Templates
                            </span>
                        </div>
                    </div>
                </div>
            )}

            <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-6">
                <div className="flex">
                    <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 002 0v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                    </div>
                    <div className="ml-3">
                        <h3 className="text-sm font-medium text-blue-800">Template Configuration Guide</h3>
                        <div className="mt-2 text-sm text-blue-700">
                            <p className="mb-2">The system uses a two-level template structure:</p>
                            <ul className="list-disc pl-5 space-y-1">
                                <li><strong>Master Template</strong>: Defines the global layout (branding, header, footer). MUST contain the <code>{'{{content}}'}</code> placeholder where the body will be injected.</li>
                                <li><strong>Content Templates</strong>: Define the specific body for each email type (e.g., Alerts, Welcome Email). These are injected into the Master template.</li>
                            </ul>
                            <p className="mt-2">Use the <strong>Default</strong> checkbox to ensure a template is used automatically by the system.</p>
                        </div>
                    </div>
                </div>
            </div>

            {isReadOnly && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
                    <p className="text-sm text-amber-800">
                        Viewing system default templates (read-only). Switch to <strong>Custom Templates</strong> mode to create your own.
                    </p>
                </div>
            )}

            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Subject</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Default</th>
                            {!isReadOnly && (
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                            )}
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {templates.map((template) => (
                            <tr key={template.id} className="hover:bg-gray-50">
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                    {template.name}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${template.type === 'master' ? 'bg-purple-100 text-purple-800' : 'bg-green-100 text-green-800'
                                        }`}>
                                        {template.type}
                                    </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    {template.subject || '-'}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    {template.is_default && <Check className="w-4 h-4 text-green-500" />}
                                </td>
                                {!isReadOnly && (
                                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                                        {canUpdate && (
                                            <button
                                                onClick={() => handleEdit(template)}
                                                className="text-primary-600 hover:text-primary-900"
                                            >
                                                <Edit2 className="w-4 h-4" />
                                            </button>
                                        )}
                                        {canDelete && (
                                            <button
                                                onClick={() => handleDelete(template.id)}
                                                className="text-red-600 hover:text-red-900"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        )}
                                    </td>
                                )}
                            </tr>
                        ))}
                        {templates.length === 0 && (
                            <tr>
                                <td colSpan={isReadOnly ? 4 : 5} className="px-6 py-8 text-center text-gray-500">
                                    {isReadOnly
                                        ? 'No system templates configured.'
                                        : 'No custom templates yet. Create one or use "Load Defaults" to start from system templates.'}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Confirm Delete Dialog */}
            <ConfirmDialog
                isOpen={confirmState.show}
                onClose={() => setConfirmState({ show: false })}
                onConfirm={confirmDelete}
                title="Delete Template"
                message="Are you sure you want to delete this template?"
                confirmText="Delete"
                variant="danger"
                isLoading={actionLoading}
            />
        </div>
    );
};

export default EmailTemplates;
